import React from 'react';
import { BrainCircuit, Cpu, Gauge, Layers3, MemoryStick, Monitor } from 'lucide-react';
import { makeOverrideIcon } from './AppIcon';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useSystemMonitor } from '../hooks/useSystemMonitor';

interface SystemMonitorWidgetProps {
  onOpenTaskManager: () => void;
}

const CpuMetricIcon = makeOverrideIcon('cpu', Cpu);
const RamMetricIcon = makeOverrideIcon('ram', MemoryStick);
const GpuMetricIcon = makeOverrideIcon('gpu', Monitor);
const WebGpuMetricIcon = makeOverrideIcon('webgpu', BrainCircuit);
const StackMetricIcon = makeOverrideIcon('stack', Layers3);

export const SystemMonitorWidget: React.FC<SystemMonitorWidgetProps> = ({ onOpenTaskManager }) => {
  const snapshot = useSystemMonitor();
  const memoryPercent = snapshot.memory.systemUsagePercent ?? snapshot.memory.heapUsagePercent;
  const gpuLabel = snapshot.gpu.systemModel || snapshot.gpu.webglRenderer;

  return (
    <div className="pointer-events-auto absolute right-5 top-5 z-10 hidden w-[320px] lg:block">
      <div className="overflow-hidden rounded-[28px] border border-border/60 bg-card/65 shadow-[0_24px_80px_-30px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
        <div className="relative overflow-hidden border-b border-border/60 bg-gradient-to-br from-primary/18 via-background/75 to-accent/25 px-5 py-5">
          <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-primary/20 blur-2xl" />
          <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-accent/40 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="p-0.5 text-primary">
                  <StackMetricIcon size={24} className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Desktop monitor</div>
                  <div className="text-lg font-semibold">System Dock</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Gauge className="h-3.5 w-3.5" />
                {snapshot.runtime.fps} FPS • {snapshot.runtime.environment === 'electron' ? 'Electron desktop' : 'Browser sandbox'}
              </div>
            </div>
            <Badge variant={snapshot.runtime.environment === 'electron' ? 'default' : 'secondary'} className="rounded-full px-3 py-1">
              {snapshot.runtime.environment === 'electron' ? 'Desktop' : 'Browser'}
            </Badge>
          </div>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 font-medium"><CpuMetricIcon size={18} className="h-[18px] w-[18px] text-primary" /> CPU</div>
              <span>{snapshot.cpu.available ? `${snapshot.cpu.usagePercent}%` : '—'}</span>
            </div>
            <Progress value={snapshot.cpu.usagePercent ?? 0} className="h-2.5" />
            {!snapshot.cpu.available ? <div className="mt-1 text-xs text-muted-foreground">Desktop-only telemetry</div> : null}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 font-medium"><RamMetricIcon size={18} className="h-[18px] w-[18px] text-primary" /> RAM</div>
              <span>{memoryPercent}%</span>
            </div>
            <Progress value={memoryPercent} className="h-2.5" />
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl border border-border/50 bg-background/60 p-3">
              <div className="flex items-center gap-2 text-muted-foreground"><GpuMetricIcon size={18} className="h-[18px] w-[18px]" /> GPU</div>
              <div className="mt-2 line-clamp-2 font-medium">{gpuLabel}</div>
            </div>
            <div className="rounded-2xl border border-border/50 bg-background/60 p-3">
              <div className="flex items-center gap-2 text-muted-foreground"><WebGpuMetricIcon size={18} className="h-[18px] w-[18px]" /> WebGPU</div>
              <div className="mt-2 line-clamp-2 font-medium">{snapshot.gpu.webgpuAdapterName || 'Not available'}</div>
            </div>
          </div>

          <Button className="w-full rounded-2xl" onClick={onOpenTaskManager}>
            Open Task Manager
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitorWidget;